// ============================================================================
// CLEO'S SECRET — RequireAdmin gate wrapper
// File: src/components/RequireAdmin.jsx
//
// Wraps a clinical/admin route component. Mirrors the gate pattern lifted
// from AdminPanel.jsx — POSTs to /api/check-admin with the patient's email,
// renders a spinner while resolving, renders an access-denied screen if the
// caller is not an admin, otherwise renders children.
//
// Current gate: STRICT patients.is_admin === true. Does not consult
// patient_roles. See api/check-admin.js — the server-side endpoint queries
// only patients.is_admin and ignores any patient_roles.role value.
//
// Post-DHF auth hardening session:
//   - Broaden /api/check-admin (or split into /api/check-clinical-role) to
//     also accept patient_roles.role IN ('clinician','manager','gp'). The
//     first non-admin clinician added (e.g. Robyn before her is_admin flag
//     flips) will otherwise be denied by this gate.
//   - Add server-side auth on every /api/admin-* /api/clinical-* and
//     patient-scoped endpoint so the frontend gate stops being the only
//     line of defence.
//   - RLS rollout on PHI tables.
//
// Used by ClinicalDashboard, PathologyReview, RecommendationBuilder,
// ClinicalCopilot, RetentionPage. NOT used by AdminPanel — that page keeps
// its own gate because it has an inline AdminSignInGate fallback for
// password-based admin sign-in; clinical pages don't.
//
// Render contract:
//   - !email + waiting (waitMs < 3000)     → spinner (auth rehydrating)
//   - !email + waited  (waitMs >= 3000)    → access denied (not signed in)
//   - email + checking || isAdmin === null → spinner
//   - email + isAdmin === false            → access denied
//   - email + isAdmin === true             → children
//
// Cold-mount behavior: on direct navigation to /?page=clinical, App.jsx's
// getCurrentUser() is async — patientEmail starts null and only resolves
// once Supabase rehydrates the session from localStorage. Without the 3s
// settle, RequireAdmin would deny immediately, flicker through spinner,
// and land on content (for admins) or denial (for patients). The settle
// loop mirrors AdminPanel's emailWaitMs pattern so cold mounts of admins
// land smoothly. After 3s with no email, the user is genuinely not signed
// in and AccessDenied is correct. AdminPanel falls through to
// AdminSignInGate on timeout because admin sign-in is password-based;
// clinical pages have no password-login fallback, so timeout → AccessDenied.
//
// Children are not mounted until the admin check clears, so the gated
// page's data-fetching useEffects never fire for non-admins — frontend
// gate doubles as a (partial) data-fetch suppressor. The /api/* endpoints
// themselves are still unauthenticated; this is a UX gate, not a security
// boundary. See the post-DHF auth hardening notes above.
//
// Props:
//   - email      string | null   the signed-in patient's email; if null,
//                                user is unauthenticated → denied
//   - isAdmin    boolean | null  if parent has already resolved this
//                                (App.jsx does), trust it and skip the
//                                fetch — mirrors AdminPanel's isAdminProp
//                                short-circuit
//   - onNavigate (dest) => void  used by the "Go to my portal" CTA
//   - children   ReactNode       rendered only when isAdmin === true
// ============================================================================

import React, { useState, useEffect } from 'react';
import { FF, P, BtnPrimary, Spinner } from '../lib/design-tokens';
import Ankh from './Ankh';

export default function RequireAdmin(props) {
  var email = props.email;
  var isAdminProp = props.isAdmin;
  var onNavigate = props.onNavigate;
  var children = props.children;

  var adminCheckState = useState({ checking: false, isAdmin: null });
  var adminCheck = adminCheckState[0];
  var setAdminCheck = adminCheckState[1];

  // Email-rehydration settle loop. Increments in 500ms steps up to 3000ms
  // while email is unresolved, so cold mounts don't flicker through
  // AccessDenied before Supabase finishes restoring the session. Pattern
  // lifted from AdminPanel.jsx.
  var emailWaitState = useState(0);
  var emailWaitMs = emailWaitState[0];
  var setEmailWaitMs = emailWaitState[1];

  useEffect(function() {
    if (email) return;
    if (emailWaitMs >= 3000) return;
    var t = setTimeout(function() { setEmailWaitMs(emailWaitMs + 500); }, 500);
    return function() { clearTimeout(t); };
  }, [email, emailWaitMs]);

  useEffect(function() {
    if (!email) return;
    // If parent already told us isAdmin, trust that and skip the fetch
    if (typeof isAdminProp === 'boolean') {
      setAdminCheck({ checking: false, isAdmin: isAdminProp });
      return;
    }
    setAdminCheck({ checking: true, isAdmin: null });
    fetch('/api/check-admin', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email }),
    })
      .then(function(r) { return r.json(); })
      .then(function(data) {
        setAdminCheck({
          checking: false,
          isAdmin: (data && data.isAdmin === true),
        });
      })
      .catch(function() {
        setAdminCheck({ checking: false, isAdmin: false });
      });
  }, [email, isAdminProp]);

  // No email yet AND still inside the rehydration window → spinner.
  // Supabase may still be restoring the session from localStorage; denying
  // here would flicker through AccessDenied before the admin landed.
  if (!email && emailWaitMs < 3000) {
    return (
      <div style={{
        background: P.PAP, minHeight: '100vh',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Spinner />
      </div>
    );
  }

  // Settle window expired and still no email → user is not signed in.
  // Clinical pages have no password-login fallback (unlike AdminPanel),
  // so deny rather than show a sign-in form.
  if (!email) {
    return <AccessDenied onNavigate={onNavigate} />;
  }

  // Email present, admin check in progress → spinner
  if (adminCheck.checking || adminCheck.isAdmin === null) {
    return (
      <div style={{
        background: P.PAP, minHeight: '100vh',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Spinner />
      </div>
    );
  }

  // Not an admin → access denied
  if (adminCheck.isAdmin !== true) {
    return <AccessDenied onNavigate={onNavigate} />;
  }

  // Cleared → render the gated content
  return children;
}

function AccessDenied(props) {
  var onNavigate = props.onNavigate;
  return (
    <div style={{
      background: P.PAP, minHeight: '100vh',
      padding: 'clamp(64px, 10vh, 120px) 24px',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    }}>
      <div style={{ maxWidth: 520, width: '100%', textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 32 }}>
          <Ankh responsive="opener" color={P.GO} />
        </div>
        <p style={{
          fontFamily: FF, fontSize: 'clamp(22px, 2.6vw, 32px)',
          fontStyle: 'italic', color: P.BR2,
          margin: '0 0 22px', lineHeight: 1.35,
        }}>
          This area is for staff.
        </p>
        <h1 style={{
          fontFamily: FF, fontWeight: 400,
          color: P.INK, marginTop: 0, marginBottom: 24,
          fontSize: 'clamp(34px, 4.4vw, 52px)',
          lineHeight: 1.1, letterSpacing: '-0.014em',
        }}>
          You don't have access to this area.
        </h1>
        <p style={{
          fontFamily: FF, fontSize: 'clamp(16px, 1.8vw, 19px)',
          color: P.TL, lineHeight: 1.65,
          maxWidth: 420, margin: '0 auto 36px',
        }}>
          If you think this is a mistake, contact Harpinder.
        </p>
        <BtnPrimary onClick={function() { if (onNavigate) onNavigate('portal'); }}>
          Go to my portal
        </BtnPrimary>
      </div>
    </div>
  );
}
